import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import { Box } from '@mui/material';
import useAlias from '@src/hooks/useAlias';
import { useSOCConfig } from '@src/hooks/useSOCConfig';
import useSubscription from '@src/hooks/useSubscription';
import storeConnector from '@src/store/storeConnector';
import { Actions } from '@src/types/Actions';
import { DataPoint } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypePointList } from '@src/types/Widget';
import { useMemo } from 'react';
import ProgressBar from './ProgressBar/ProgressBar';

interface SiteSOCProps {
  siteMeta: SiteMeta;
  actions: Actions;
}

interface SOCProgressBarProps {
  datapoint?: DataPoint[];
  max: number;
  min: number;
  isLoading?: boolean;
}

function SOCProgressBar({
  datapoint,
  max,
  min,
  isLoading,
}: SOCProgressBarProps) {
  const subscribedDataPoints = useMemo(() => datapoint ?? [], [datapoint]);
  const dpWVal = useSubscription(subscribedDataPoints);
  const value = dpWVal[0]?.value ?? (0 as number);
  const alias = useAlias();

  return (
    <ProgressBar
      title={alias('SOC')}
      value={value}
      loading={isLoading}
      styleClass={min > +value || +value > max ? 'red' : ''}
    />
  );
}

function SiteSOC({ siteMeta, actions }: SiteSOCProps) {
  const { data: widgetConfig, isLoading } =
    useGetInternalWidgetConfig<WidgetTypePointList>(
      'site_SOC',
      {},
      {
        queryKeys: ['site_soc'],
      },
    );

  const socRange = useSOCConfig(actions, siteMeta.siteId);
  const socDataPoint = useMemo(
    () => widgetConfig?.dataPoints?.[0] ?? undefined,
    [widgetConfig?.dataPoints],
  );

  return (
    <Box>
      <SOCProgressBar
        datapoint={socDataPoint}
        max={socRange.max}
        min={socRange.min}
        isLoading={isLoading}
      />
    </Box>
  );
}

export default storeConnector(SiteSOC, {
  user: ['role'],
  config: ['siteMeta'],
});
